import { useState, useEffect, useCallback } from 'react';
import './VenusHistory.css';

const timeline = [
    {
        year: '1850s',
        title: 'First Settlers Arrive',
        body: 'Originally known as Gossip and later Midway, the Venus area welcomed its first settlers in the late 1850s. Midway was founded by Samuel B. Truelove, who donated 90 acres for a townsite that included a grocery store, Baptist Church, Post Office, and Dr. Boles\' office.',
        photo: '/assets/images/history/Venus-panoramic.jpg',
        photoCaption: 'Panoramic view of early Venus, c. 1890',
    },
    {
        year: '1881',
        title: 'The First Railroad',
        body: 'The first railroad came through the Venus area in 1881, running from Dallas to Cleburne through the present site of Venus, opening the region to commerce and agriculture.',
        photo: '/assets/images/history/venus-hotel.jpg',
        photoCaption: 'The Venus Hotel — near the Santa Fe Depot, 1923',
    },
    {
        year: '1893',
        title: 'A Town Is Born',
        body: 'J.C. Smyth purchased 80 acres in a cornfield next to the railroad and laid off town lots. Dr. Fontaine looked over the land and said, "What a beautiful prairie" — inspiring the name Venus, after the Greek goddess of beauty.',
        photo: '/assets/images/history/venus-bank.jpg',
        photoCaption: "Venus Bank / Farmer's Merchant Bank — first bank founded by J.C. Smyth",
    },
    {
        year: '1888 – 1895',
        title: 'Growth & Infrastructure',
        body: 'A post office opened in 1888. By 1890, Venus had ten residents and two railroad lines. The Santa Fe Depot opened August 2, 1895, connecting Venus to Fort Worth and Waco.',
        photo: '/assets/images/history/Venus-gin.jpg',
        photoCaption: 'Venus Gin Co. — hub of agricultural commerce',
    },
    {
        year: '1896',
        title: 'Burnetta College Opens',
        body: 'A four-story frame college opened September 7, 1896 with 250 students, named Burnetta College in honor of Burnetta Barnes. At its peak, 325 students were enrolled. The building burned around 1906.',
        photo: '/assets/images/history/burnetta-college.jpg',
        photoCaption: 'Burnetta College, founded 1896',
        photo2: '/assets/images/history/Russell-family.jpg',
        photo2Caption: 'Prof. Will Russell & family — among the first educators of Venus',
    },
    {
        year: '1896 – 1895',
        title: 'Faith & Community',
        body: 'The First Baptist Church was organized August 25, 1896. The First Methodist Church arrived in 1895. The Church of Christ followed in the early 1900s — forming the spiritual backbone of the young town.',
        photo: '/assets/images/history/first-baptist-church.jpg',
        photoCaption: 'First Baptist Church of Venus, 1896',
    },
    {
        year: '1903',
        title: 'Incorporation',
        body: 'Venus was incorporated in 1903. The first City Council met July 8, 1903. City Hall on the square doubled as a jail with two cells and a "Domino Hall" upstairs.',
        photo: '/assets/images/history/city-hall.jpg',
        photoCaption: 'Venus City Hall – Jailhouse, early 1900s',
    },
    {
        year: 'Early 1900s',
        title: 'A Thriving Square',
        body: 'Venus reported thirteen businesses, thirty-one houses, and three banks. The square was lined with a furniture store, drugstore, tailor, barber, hardware, groceries, and more.',
        photo: '/assets/images/history/north-square-1900.jpg',
        photoCaption: 'North Square, early 1900s',
    },
    {
        year: '1912',
        title: 'The Venus Express',
        body: 'Citizens welcomed the Venus Express, a weekly newspaper founded by Sam Brazwell, keeping the community informed as Venus grew into a regional hub for trade and agriculture.',
        photo: '/assets/images/history/west-square-1920.jpg',
        photoCaption: 'West Side of Square, 1920',
    },
    {
        year: '1914 – 1922',
        title: 'Venus High School',
        body: 'A three-story brick school with white columns stood at 3rd and Beech St. Students attended daily chapel at 8:30 A.M. The building burned in 1922 and was later rebuilt.',
        photo: '/assets/images/history/venus-high-school.jpg',
        photoCaption: 'Venus High School, 1914',
    },
    {
        year: '1920s',
        title: 'The Cotton Capital',
        body: 'With a population surpassing 800, Venus was widely considered the cotton capital of the world. Three banks operated in town and the square was paved in 1920.',
        photo: '/assets/images/history/first-national-bank.jpg',
        photoCaption: 'First National Bank, 1915',
    },
    {
        year: '1927 – 1930',
        title: 'A New School Rises',
        body: 'After the 1922 fire, a new three-story brick school was built and stood until 1940. A replacement building was dedicated in 1942 — the same one that burned on February 29, 1984.',
        photo: '/assets/images/history/venus-school-1927.jpg',
        photoCaption: 'Venus School, 1927–1930',
    },
    {
        year: '1967 · 1989 · 2022',
        title: 'Hollywood Comes to Venus',
        body: 'The historic streets of Venus have attracted Hollywood three times. The Oscar-winning Bonnie and Clyde (1967), starring Faye Dunaway and Warren Beatty, used Venus as a backdrop. Then came Born on the Fourth of July (1989), directed by Oliver Stone and starring Tom Cruise. Most recently, the hit series Yellowstone (2022) filmed scenes in Venus, cementing the town\'s place in American screen history.',
        photos: [
            { src: '/assets/images/history/Bonnie1967.jpeg',          caption: 'Bonnie and Clyde (1967) — filmed in Venus, TX' },
            { src: '/assets/images/history/Born4th1989 movie.jpeg',   caption: 'Born on the Fourth of July (1989) — Oliver Stone / Tom Cruise' },
            { src: '/assets/images/history/Yellowstone2022.jpeg',     caption: 'Yellowstone (2022) — filming in Venus' },
        ],
    },
    {
        year: '1999',
        title: 'Fire on the Square',
        body: 'A devastating fire destroyed the picturesque town square, including many historic businesses. The community responded with resilience, beginning the work of rebuilding.',
    },
    {
        year: 'Today',
        title: 'A City of Opportunity',
        body: 'Venus continues to grow as one of the fastest-developing communities in the DFW metroplex, building its legacy as a destination for business, families, and investment.',
    },
];

const facts = [
    { icon: '🏛', label: 'Founded', value: '1893' },
    { icon: '🚂', label: 'First Railroad', value: '1881' },
    { icon: '🎓', label: 'Burnetta College', value: '1896' },
    { icon: '📰', label: 'Venus Express', value: '1912' },
    { icon: '🌾', label: 'Peak Population', value: '800+' },
    { icon: '🎬', label: 'Bonnie & Clyde', value: '1967' },
];


const VenusHistory = () => {
    const [lightbox, setLightbox] = useState(null);

    const openLightbox = (src, caption) => setLightbox({ src, caption });
    const closeLightbox = useCallback(() => setLightbox(null), []);

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') closeLightbox(); };
        if (lightbox) document.addEventListener('keydown', onKey);
        return () => document.removeEventListener('keydown', onKey);
    }, [lightbox, closeLightbox]);

    return (
        <div className="history-page">

            {/* ── Hero ───────────────────────────────────── */}
            <section className="history-hero">
                <div className="history-hero-overlay" />
                <div className="history-hero-content container">
                    <span className="history-label">Venus, Texas</span>
                    <h1>History of <span>Venus</span></h1>
                    <p>From a prairie cornfield to a thriving community — the story of Venus, Texas spans over 160 years of resilience, faith, and growth.</p>
                </div>
                <div className="history-hero-scroll-hint">
                    <span>↓</span>
                </div>
            </section>

            {/* ── Quick Facts ─────────────────────────────── */}
            <section className="history-facts-band">
                <div className="container">
                    <div className="history-facts-grid">
                        {facts.map((f, i) => (
                            <div key={i} className="history-fact-card">
                                <span className="history-fact-icon">{f.icon}</span>
                                <span className="history-fact-value">{f.value}</span>
                                <span className="history-fact-label">{f.label}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Founder ─────────────────────────────────── */}
            <section className="section history-founder-section">
                <div className="container">
                    <div className="history-founder-wrap">
                        <div className="history-founder-deco">
                            <div className="history-founder-year-ring">1893</div>
                        </div>
                        <div className="history-founder-content">
                            <span className="history-founder-badge">Father of Venus</span>
                            <h2 className="section-title">J. C. <span>Smyth</span></h2>
                            <p>J.C. Smyth had a vision: to create a town halfway between Alvarado and Midlothian. He bought 80 acres of land and had town lots surveyed out in a cornfield next to the railroad where Venus now stands. Smyth and his co-workers built public roads leading to the new town — a hard, multi-year effort to open the land for commerce and community.</p>
                            <p>Smyth made a deal with the railroad, securing a station that gave the agricultural center its start. The first house erected in the new town belonged to its founder himself. J.C. Smyth, father of the town, rests in the Venus Cemetery.</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* ── Timeline ─────────────────────────────────── */}
            <section className="section history-timeline-section">
                <div className="container">
                    <div className="section-header text-center">
                        <h2 className="section-title">Timeline of <span>Venus</span></h2>
                        <p className="section-subtitle">Key moments that shaped the city we know today</p>
                    </div>
                    <div className="history-timeline">
                        <div className="history-timeline-line" />
                        {timeline.map((item, i) => (
                            <div key={i} className={`history-timeline-item ${i % 2 === 0 ? 'left' : 'right'}`}>
                                <div className="history-timeline-year">{item.year}</div>
                                <div className="history-timeline-connector">
                                    <div className="history-timeline-dot" />
                                </div>
                                <div className="history-timeline-card">
                                    {item.photos && (
                                        <div className="history-timeline-photos has-multi">
                                            {item.photos.map((p, pi) => (
                                                <div key={pi} className="history-timeline-photo-wrap" onClick={() => openLightbox(p.src, p.caption)}>
                                                    <img src={p.src} alt={p.caption} className="history-timeline-photo" loading="lazy" />
                                                    <div className="history-photo-zoom-hint">🔍</div>
                                                    {p.caption && <span className="history-timeline-photo-caption">{p.caption}</span>}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {!item.photos && item.photo && (
                                        <div className={`history-timeline-photos ${item.photo2 ? 'has-two' : ''}`}>
                                            <div className="history-timeline-photo-wrap" onClick={() => openLightbox(item.photo, item.photoCaption)}>
                                                <img src={item.photo} alt={item.photoCaption} className="history-timeline-photo" loading="lazy" />
                                                <div className="history-photo-zoom-hint">🔍</div>
                                                {item.photoCaption && <span className="history-timeline-photo-caption">{item.photoCaption}</span>}
                                            </div>
                                            {item.photo2 && (
                                                <div className="history-timeline-photo-wrap" onClick={() => openLightbox(item.photo2, item.photo2Caption)}>
                                                    <img src={item.photo2} alt={item.photo2Caption} className="history-timeline-photo" loading="lazy" />
                                                    <div className="history-photo-zoom-hint">🔍</div>
                                                    {item.photo2Caption && <span className="history-timeline-photo-caption">{item.photo2Caption}</span>}
                                                </div>
                                            )}
                                        </div>
                                    )}
                                    <h3>{item.title}</h3>
                                    <p>{item.body}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Landmarks ───────────────────────────────── */}
            <section className="section section-alt">
                <div className="container">
                    <div className="section-header text-center">
                        <h2 className="section-title">Historic <span>Landmarks</span></h2>
                        <p className="section-subtitle">Places that defined the character of Venus</p>
                    </div>
                    <div className="history-landmarks-grid">
                        {[
                            { icon: '🏦', title: 'First National Bank', body: 'Established in 1915 at the corner of 2nd Street and Main Street. Venus also hosted Farmer\'s Merchant Bank and Citizen\'s Bank — three banks in a single small town, reflecting its remarkable prosperity.' },
                            { icon: '🏨', title: 'The Venus Hotel', body: 'A two-story structure with approximately 17 rooms, located west of Main Street between the Square and the Santa Fe Depot. Known as the "Venus Hotel at Santa Fe," it burned down in April 1923.' },
                            { icon: '🚂', title: 'Santa Fe Depot', body: 'Opened August 2, 1895, with J.E. Kennedy as first agent. The depot connected Venus to Fort Worth and Waco via the I.G.N. crossing and was the lifeblood of commerce for the region.' },
                            { icon: '🏫', title: 'Burnetta College Dorm', body: 'Located west of the college on Hickory Street, this 35-room structure housed up to 75 boarding students. After the college closed, it became a boarding house for the community.' },
                            { icon: '⛪', title: 'First Baptist Church', body: 'Officially organized August 25, 1896. It survived two fires — the 1920s and January 15, 1961 — and was rebuilt each time. The present brick building still stands today.' },
                            { icon: '🏛', title: 'Venus City Hall', body: 'A two-story frame structure on the city square, built in the early 1900s. It served as both city hall and jail — with 2 cells downstairs and a "Domino Hall" upstairs. First council meeting: July 8, 1903.' },
                        ].map((lm, i) => (
                            <div key={i} className="history-landmark-card">
                                <div className="history-landmark-icon">{lm.icon}</div>
                                <h3>{lm.title}</h3>
                                <p>{lm.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* ── Quote ───────────────────────────────────── */}
            <section className="section history-quote-section">
                <div className="history-quote-inner container">
                    <div className="history-quote-mark">"</div>
                    <blockquote className="history-quote">
                        <p>What a beautiful prairie.</p>
                        <cite>— Dr. Fontaine, 1893 · inspiring the name Venus after the Greek goddess of beauty</cite>
                    </blockquote>
                </div>
            </section>

            {/* ── Lightbox ────────────────────────────────── */}
            {lightbox && (
                <div className="history-lightbox-backdrop" onClick={closeLightbox}>
                    <div className="history-lightbox" onClick={(e) => e.stopPropagation()}>
                        <button className="history-lightbox-close" onClick={closeLightbox} aria-label="Close">✕</button>
                        <img src={lightbox.src} alt={lightbox.caption} className="history-lightbox-img" />
                        {lightbox.caption && <p className="history-lightbox-caption">{lightbox.caption}</p>}
                    </div>
                </div>
            )}

        </div>
    );
};

export default VenusHistory;
